import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import type { } from '@backoffice-frontend/notifications/util-api';
import type { LimitReportBuySignal, LimitReportGasSignal, LimitReportOtherSignal, LimitReportPowerSignal, Message, Notification } from '@clean-code/models';
import { NotificationItemComponent } from '@clean-code/notifications/feature-notification-center';
import { DateParserService } from '@clean-code/shared/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faBorderAll,
  faBurn,
  faExclamationTriangle,
  faPlug,
} from '@fortawesome/pro-solid-svg-icons';
import { TranslocoModule } from '@jsverse/transloco';

const LIMIT_REPORT_POWER_SIGNAL_TYPE = 'LimitReportPowerSignal';
const LIMIT_REPORT_GAS_SIGNAL_TYPE = 'LimitReportGasSignal';
const LIMIT_REPORT_OTHER_SIGNAL_TYPE = 'LimitReportOtherSignal';

@Component({
  selector: 'backoffice-frontend-limit-report-buy-signal-notification-item',
  templateUrl: './buy-signal-notification-item.component.html',
  styleUrls: ['./buy-signal-notification-item.component.scss'],
  imports: [
    DatePipe,
    DecimalPipe,
    MatTooltipModule,
    FontAwesomeModule,
    TranslocoModule,
    NotificationItemComponent,
  ],
})
export class BuySignalNotificationItemComponent implements Notification {
  private router = inject(Router);

  public faBurn = faBurn;
  public faPlug = faPlug;
  public faBorderAll = faBorderAll;
  public faExclamationTriangle = faExclamationTriangle;

  public message = input.required<Message>();

  public isPowerSignal = computed(
    () => this.message()?.type === LIMIT_REPORT_POWER_SIGNAL_TYPE,
  );

  public isGasSignal = computed(
    () => this.message()?.type === LIMIT_REPORT_GAS_SIGNAL_TYPE,
  );

  public isOtherSignal = computed(
    () => this.message()?.type === LIMIT_REPORT_OTHER_SIGNAL_TYPE,
  );

  public isEnergySignal = computed(
    () => this.isPowerSignal() || this.isGasSignal() || this.isOtherSignal(),
  );

  public buySignal = computed(() => {
    const message = this.message();
    if (!this.isEnergySignal() && message?.data) {
      return <LimitReportBuySignal>(
        JSON.parse(message.data, DateParserService.getJsonDateReviver())
      );
    }
    return {} as LimitReportBuySignal;
  });

  public powerSignal = computed(() => {
    const message = this.message();
    if (this.isPowerSignal() && message?.data) {
      return <LimitReportPowerSignal>(
        JSON.parse(message.data, DateParserService.getJsonDateReviver())
      );
    }
    return {} as LimitReportPowerSignal;
  });

  public gasSignal = computed(() => {
    const message = this.message();
    if (this.isGasSignal() && message?.data) {
      return <LimitReportGasSignal>(
        JSON.parse(message.data, DateParserService.getJsonDateReviver())
      );
    }
    return {} as LimitReportGasSignal;
  });

  public otherSignal = computed(() => {
    const message = this.message();
    if (this.isOtherSignal() && message?.data) {
      return <LimitReportOtherSignal>(
        JSON.parse(message.data, DateParserService.getJsonDateReviver())
      );
    }
    return {} as LimitReportOtherSignal;
  });

  public powerStrategy = computed(() => this.message()?.action ?? '');

  public powerStrategyKey = computed(
    () =>
      `limit-reports.notification.limitreportpowersignal.ACTION.${this.powerStrategy()}`,
  );

  public gasStrategyKey = computed(
    () =>
      `limit-reports.notification.limitreportgassignal.ACTION.${this.powerStrategy()}`,
  );

  public otherStrategyKey = computed(
    () =>
      `limit-reports.notification.limitreportothersignal.ACTION.${this.powerStrategy()}`,
  );

  public itemClicked(/*$event: Event*/): void {
    if (this.isPowerSignal()) {
      const id = this.powerSignal().limitReport?.id;
      if (id) {
        void this.router.navigate([
          `/limit-reports/power/limit-report/${id}/overview`,
        ]);
      }
      return;
    }
    if (this.isGasSignal()) {
      const id = this.gasSignal().limitReport?.id;
      if (id) {
        void this.router.navigate([
          `/limit-reports/gas/limit-report/${id}/overview`,
        ]);
      }
      return;
    }
    if (this.isOtherSignal()) {
      const id = this.otherSignal().limitReport?.id;
      if (id) {
        void this.router.navigate([
          `/limit-reports/other/limit-report/${id}/overview`,
        ]);
      }
      return;
    }
    void this.router.navigate([
      `/limit-reports/${this.buySignal().energySource.toLowerCase()}/limit-report/${this.buySignal().limitReport.id}/overview`,
    ]);
  }
}
